import React, { useEffect, useState, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CreateProjectModal from "../modals/CreateProjectModal";
import Icon from "../common/Icon";
import styles from "./Sidebar.module.css";

import { createEmptyProject, getProjects } from "../../api";
import myProjectsLogo from "../../assets/images/creation-my-projects.png";

interface SidebarProps {
    projectLinks: { name: string; path: string; logo: string }[];
    fetchProjects?: () => Promise<void>;
    contentType?: "text" | "cartel" | "newsletter";
}

const PATH_ICONS: Record<string, string> = {
    "/create/text": "ic-pencil",
    "/create/text-remix": "ic-shuffle",
    "/create/text-to-speech": "ic-volume",
    "/create/sound-library": "ic-music",
    "/create/audio-editing": "ic-scissors",
    "/create/voices": "ic-audio-lines",
    "/create/translation": "ic-languages",
    "/create/cartel": "ic-pencil",
    "/create/cartel-remix": "ic-shuffle",
    "/create/cartel-translation": "ic-languages",
    "/create/newsletter": "ic-pencil",
    "/create/newsletter-remix": "ic-shuffle",
    "/create/newsletter-translation": "ic-languages",
};

const Sidebar: React.FC<SidebarProps> = ({ projectLinks, fetchProjects, contentType = "text" }) => {
    const [filteredProjects, setFilteredProjects] = useState<{ name: string; path: string; logo: string }[]>([]);
    const [emptyProjects, setEmptyProjects] = useState<{ name: string; path: string; logo: string }[]>([]);
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const location = useLocation();
    const navigate = useNavigate();
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

    const draftPath =
        contentType === "cartel"
            ? "/create/cartel-drafts"
            : contentType === "newsletter"
              ? "/create/newsletter-drafts"
              : "/create/drafts";

    const trashPath =
        contentType === "cartel"
            ? "/create/cartel-trash"
            : contentType === "newsletter"
              ? "/create/newsletter-trash"
              : "/create/trash";

    const fetchFilteredProjects = useCallback(async () => {
        const token = localStorage.getItem("token");
        if (!token) return;

        try {
            const itemType = contentType === "text" ? "audio" : contentType;
            const response = await getProjects({ token, itemType });
            if (!response) return;

            const projectsWithDetails = response.map((project: any) => {
                let isProjectEmpty = false;

                if (contentType === "cartel") {
                    isProjectEmpty = !project.cartelAI || project.cartelAI.length === 0;
                } else if (contentType === "newsletter") {
                    isProjectEmpty = !project.newsletterAI || project.newsletterAI.length === 0;
                } else {
                    isProjectEmpty = !project.audioAI || project.audioAI.length === 0;
                }

                return {
                    name: project.name,
                    path:
                        contentType === "cartel"
                            ? `/cartel-projects/${project.id}`
                            : contentType === "newsletter"
                              ? `/newsletter-projects/${project.id}`
                              : `/projects/${project.id}`,
                    logo: myProjectsLogo,
                    isEmpty: isProjectEmpty,
                };
            });

            setFilteredProjects(projectsWithDetails.filter((p: { isEmpty: boolean }) => !p.isEmpty));
            setEmptyProjects(projectsWithDetails.filter((p: { isEmpty: boolean }) => p.isEmpty));
        } catch (err) {
            console.error("Failed to fetch filtered projects:", err);
            setFilteredProjects(projectLinks);
            setEmptyProjects([]);
        }
    }, [contentType, projectLinks]);

    useEffect(() => {
        fetchFilteredProjects();
    }, [fetchFilteredProjects]);

    const getNavLinks = () => {
        switch (contentType) {
            case "cartel":
                return [
                    { name: t("textGeneration"), path: "/create/cartel" },
                    { name: t("textRemix"), path: "/create/cartel-remix" },
                    { name: t("translation"), path: "/create/cartel-translation" },
                ];
            case "newsletter":
                return [
                    { name: t("textGeneration"), path: "/create/newsletter" },
                    { name: t("textRemix"), path: "/create/newsletter-remix" },
                    { name: t("translation"), path: "/create/newsletter-translation" },
                ];
            case "text":
            default:
                return [
                    { name: t("textGeneration"), path: "/create/text" },
                    { name: t("textRemix"), path: "/create/text-remix" },
                    { name: t("textToSpeech"), path: "/create/text-to-speech" },
                    { name: t("soundLibrary"), path: "/create/sound-library" },
                    { name: t("audioEditing"), path: "/create/audio-editing" },
                    { name: t("voices"), path: "/create/voices" },
                    { name: t("translation"), path: "/create/translation" },
                ];
        }
    };

    const navLinks = getNavLinks();

    useEffect(() => {
        if (fetchProjects) {
            fetchProjects();
        }
    }, [fetchProjects]);

    useEffect(() => {
        if (projectLinks.length > 0) {
            fetchFilteredProjects();
        }
    }, [projectLinks, fetchFilteredProjects]);

    const handleAddToProject = async (newProjectName: string) => {
        const token = localStorage.getItem("token");
        if (!token) return;

        const resp = await createEmptyProject({ token, name: newProjectName });
        if (fetchProjects) {
            await fetchProjects();
        }
        await fetchFilteredProjects();

        if (resp?.id) {
            if (contentType === "cartel") {
                navigate(`/cartel-projects/${resp.id}/add-item`);
            } else if (contentType === "newsletter") {
                navigate(`/newsletter-projects/${resp.id}/add-item`);
            } else {
                navigate(`/projects/${resp.id}/add-item`);
            }
        }
    };

    const getSidebarTitle = () => {
        switch (contentType) {
            case "cartel":
                return t("cartelContentCreation");
            case "newsletter":
                return t("newsletterContentCreation");
            case "text":
            default:
                return t("audioContentCreation");
        }
    };

    const isPathActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + "/");

    const itemClass = (path: string) => `${styles.item}${isPathActive(path) ? ` ${styles.itemActive}` : ""}`;

    const projectsLabel =
        contentType === "cartel"
            ? t("myCartelProjectsAllCaps") || t("myProjectsAllCaps")
            : contentType === "newsletter"
              ? t("myNewsletterProjectsAllCaps") || t("myProjectsAllCaps")
              : t("myProjectsAllCaps");

    return (
        <aside className={styles.sidebar}>
            <div className={styles.topNav}>
                <div className={styles.title}>{getSidebarTitle()}</div>

                <div className={styles.groupLabel}>{t("createAllCaps")}</div>
                <ul className={styles.navList}>
                    {navLinks.map((link) => (
                        <li key={link.path}>
                            <Link to={link.path} className={itemClass(link.path)}>
                                <Icon name={PATH_ICONS[link.path] || "ic-file-text"} className={`ic ${styles.icon}`} />
                                {link.name}
                            </Link>
                        </li>
                    ))}
                </ul>

                <div className={styles.groupLabel}>{t("withoutProjectAllCaps")}</div>
                <ul className={styles.navList}>
                    <li>
                        <Link to={draftPath} className={itemClass(draftPath)}>
                            <Icon name="ic-file-text" className={`ic ${styles.icon}`} />
                            {t("drafts")}
                        </Link>
                    </li>
                </ul>

                <div className={styles.groupLabel}>
                    <span>{projectsLabel}</span>
                    <button type="button" className={styles.createBtn} onClick={() => setIsModalOpen(true)}>
                        + {t("create")}
                    </button>
                </div>
            </div>

            <div className={styles.projectsScroll}>
                <ul className={styles.navList}>
                    {filteredProjects?.map((link) => (
                        <li key={link.path}>
                            <Link to={link.path} className={itemClass(link.path)} title={link.name}>
                                <Icon name="ic-folder" className={`ic ${styles.icon}`} />
                                <span className={styles.projectName}>{link.name}</span>
                            </Link>
                        </li>
                    ))}
                </ul>

                {emptyProjects.length > 0 && (
                    <div className={styles.emptyGroup}>
                        <div className={styles.groupLabel}>
                            {t("emptyProjectsNotClassified") || "EMPTY PROJECTS NOT CLASSIFIED"}
                        </div>
                        <ul className={styles.navList}>
                            {emptyProjects.map((link) => (
                                <li key={link.path}>
                                    <Link to={link.path} className={itemClass(link.path)} title={link.name}>
                                        <Icon name="ic-folder" className={`ic ${styles.icon}`} />
                                        <span className={styles.projectName}>{link.name}</span>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>

            <hr className={styles.divider} />
            <div className={styles.trash}>
                <ul className={styles.navList}>
                    <li>
                        <Link to={trashPath} className={itemClass(trashPath)}>
                            <Icon name="ic-trash" className={`ic ${styles.icon}`} />
                            {t("trash")}
                        </Link>
                    </li>
                </ul>
            </div>

            <CreateProjectModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onAddToProject={handleAddToProject}
            />
        </aside>
    );
};

export default Sidebar;
